import { useState } from "react";
import { Link } from "react-router-dom";
import {
  mockPatient,
  financialROI,
  globalComplianceText,
  processedItems,
} from "@/mocks/demo-flow";
import ClinicHeaderUser from "@/components/feature/ClinicHeaderUser";

export default function ClinicROIPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const handleUnlock = () => {
    setUnlocking(true);
    setTimeout(() => {
      setUnlocked(true);
      setUnlocking(false);
    }, 1200);
  };

  const handleContactPatient = () => {
    setContactSent(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <ClinicHeaderUser />
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8 relative">
        {/* Subtle warm background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/15 blur-3xl"></div>
          <div className="absolute bottom-20 -left-40 w-[400px] h-[400px] rounded-full bg-primary-100/10 blur-3xl"></div>
        </div>

        {/* Page Title */}
        <div className="relative z-10">
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-1">
            The Partner Clinic Enterprise Dashboard
          </h1>
          <p className="text-xs md:text-sm text-foreground-400 max-w-xl">
            Automated profitability forecast, patient profile, and sourcing
            intelligence — powered by AI-aggregated vendor data.
          </p>
        </div>

        {/* ===== SPLIT LAYOUT: Patient File + Net Margin + Processed (Left) | SWO + Expense (Right) ===== */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ===== LEFT COLUMN: Patient Clinical Profile → Net Margin → What's Been Processed ===== */}
          <div className="flex flex-col space-y-5">
            {/* ===== SECTION 1: PATIENT CLINICAL PROFILE ===== */}
            <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-7 relative overflow-hidden">
              <h2 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-folder-user-line text-xs"></i>
                </span>
                Patient Clinical Profile
                {!unlocked && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-[9px] font-semibold text-accent-700 ml-auto">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                    LOCKED
                  </span>
                )}
                {unlocked && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-[9px] font-semibold text-green-700 ml-auto">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    UNLOCKED
                  </span>
                )}
              </h2>

              {/* ===== VISIBLE CLINICAL DATA (Always shown) ===== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className="rounded-lg border border-background-100 bg-background-50 p-3">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Amputation Typology
                  </span>
                  <p className="text-xs md:text-sm font-bold text-foreground-800">
                    {mockPatient.amputationLevel}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-3">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Verified K-Level Potential
                  </span>
                  <p className="text-xs md:text-sm font-bold text-foreground-800">
                    {mockPatient.kLevel} — {mockPatient.kLevelDescription}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-3 sm:col-span-2">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Mobility Level Goals
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {mockPatient.mobilityGoals.map((goal) => (
                      <span
                        key={goal}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-50 border border-primary-200 text-[10px] font-medium text-primary-700"
                      >
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-check-line text-[8px]"></i>
                        </span>
                        {goal}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* ===== PROTECTED PHI — Gaussian Blur Overlay ===== */}
              <div className="relative rounded-xl border border-background-200 bg-background-50 p-4 overflow-hidden">
                {/* Blur layer */}
                <div
                  className={`transition-all duration-700 ${unlocked ? "blur-0 opacity-0 pointer-events-none" : "blur-lg opacity-100"}`}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Patient Name
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {mockPatient.fullName}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Date of Birth
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {new Date(mockPatient.dateOfBirth).toLocaleDateString(
                          "en-US",
                          { month: "long", day: "numeric", year: "numeric" },
                        )}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Home ZIP Code
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {mockPatient.zipCode}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Insurance Policy ID
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {mockPatient.policyId}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Phone
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {mockPatient.phone}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Preferred Contact
                      </span>
                      <p className="text-xs font-bold text-foreground-800">
                        {mockPatient.contactMethod}
                      </p>
                    </div>
                  </div>

                  {/* 3D Scan Preview (blurred) */}
                  <div className="mt-3 rounded-lg overflow-hidden bg-foreground-50 border border-background-100 relative h-[160px]">
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-xs text-foreground-400 font-medium">
                        MyFit 3D Lower Limb Scan — Blurred for PHI Protection
                      </span>
                    </div>
                  </div>
                </div>

                {/* PAYWALL OVERLAY BOX */}
                {!unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px]" />
                    <div className="relative bg-white rounded-xl border-2 border-foreground-300 shadow-[0_8px_40px_rgba(0,0,0,0.12)] p-5 md:p-6 mx-4 text-center max-w-[420px]">
                      {/* Lock Icon */}
                      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
                        <span className="w-7 h-7 flex items-center justify-center text-foreground-600">
                          <i className="ri-lock-2-fill text-xl"></i>
                        </span>
                      </div>

                      <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-2">
                        Patient File Locked
                      </h3>
                      <p className="text-[11px] md:text-xs text-foreground-500 leading-relaxed mb-4">
                        This high-intent patient has selected your clinic and
                        completed all intake, scan, and telemedicine
                        requirements. Pay the transactional data lease fee to
                        instantly unblur and unlock the full clinical file, 3D
                        metric files, and contact channels.
                      </p>

                      {/* Value indicators */}
                      <div className="flex items-center justify-center gap-4 mb-4 text-[10px] text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-check-line text-accent-500 text-xs"></i>
                          Full PHI
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-check-line text-accent-500 text-xs"></i>
                          3D Scan Data
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-check-line text-accent-500 text-xs"></i>
                          Contact Info
                        </span>
                      </div>

                      {/* Unlock Button */}
                      {!unlocking ? (
                        <button
                          type="button"
                          onClick={handleUnlock}
                          className="w-full py-3.5 rounded-xl bg-foreground-900 text-background-50 text-xs md:text-sm font-bold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-key-2-line text-sm"></i>
                          </span>
                          Authorize Sourcing &amp; Unlock Patient File ($500)
                        </button>
                      ) : (
                        <div className="w-full py-3.5 rounded-xl bg-accent-50 border border-accent-200 flex items-center justify-center gap-2 animate-fadeSlideIn">
                          <span className="w-4 h-4 flex items-center justify-center text-accent-600">
                            <i className="ri-loader-4-line text-sm animate-spin"></i>
                          </span>
                          <span className="text-xs font-semibold text-accent-700">
                            Processing payment &amp; unlocking file...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ===== POST-UNLOCK: Triage Panel ===== */}
              {unlocked && (
                <div className="mt-5 rounded-xl border-2 border-primary-300 bg-primary-50/40 p-5 animate-fadeSlideIn">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-user-heart-line text-base"></i>
                    </span>
                    <div>
                      <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">
                        Patient Ready for Immediate Contact
                      </h3>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">
                        All intake complete. Patient is actively waiting
                        on-platform. Preferred contact:{" "}
                        <strong className="text-foreground-700">
                          {mockPatient.contactMethod}
                        </strong>
                      </p>
                    </div>
                  </div>

                  {!contactSent ? (
                    <button
                      type="button"
                      onClick={handleContactPatient}
                      className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-xs md:text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-vidicon-line text-sm"></i>
                      </span>
                      Launch VSee Telemed &amp; Contact Patient Now
                    </button>
                  ) : (
                    <div className="space-y-3 animate-fadeSlideIn">
                      <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                          <i className="ri-check-double-line text-base"></i>
                        </span>
                        <div>
                          <p className="text-sm font-bold text-green-800">
                            Patient Contacted — Awaiting Response
                          </p>
                          <p className="text-[11px] text-green-600 leading-relaxed">
                            A {mockPatient.contactMethod} notification and VSee
                            Telemed link have been sent to{" "}
                            {mockPatient.fullName}. Estimated response window:
                            under 15 minutes.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-foreground-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-checkbox-circle-fill text-xs text-green-500"></i>
                          Telemed Invite Sent
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-checkbox-circle-fill text-xs text-green-500"></i>
                          File Unlocked
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line text-xs text-accent-500"></i>
                          Awaiting Confirmation
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Compliance micro-footer for patient section */}
              <p className="text-[9px] text-foreground-400 mt-4 leading-relaxed">
                All PHI is encrypted at rest and in transit. File access is
                logged and auditable per HIPAA compliance requirements.
              </p>
            </section>

            {/* ===== SECTION 2: NET MARGIN CALCULATION ===== */}
            <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-green-100 text-green-600">
                  <i className="ri-funds-box-line text-xs"></i>
                </span>
                <div>
                  <h2 className="font-heading text-sm md:text-base font-bold text-foreground-950">
                    Net Margin Calculation
                  </h2>
                  <p className="text-[10px] md:text-[11px] text-foreground-400">
                    Based on estimated insurance reimbursement minus aggregated
                    costs
                  </p>
                </div>
              </div>

              {/* Net Profit Callout */}
              <div className="rounded-xl border-2 border-green-300 bg-green-50/60 p-5 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-green-700 font-semibold">
                      Projected Net Margin
                    </span>
                    <p className="font-heading text-3xl md:text-4xl font-bold text-green-800 mt-1">
                      {financialROI.netMarginPercent}%
                    </p>
                    <p className="text-xs md:text-sm text-green-600 font-medium mt-0.5">
                      ${financialROI.netProfit.toLocaleString()} net profit to
                      the clinic
                    </p>
                  </div>

                  {/* Donut visualization */}
                  <div className="relative w-[110px] h-[110px] flex-shrink-0">
                    <svg
                      viewBox="0 0 120 120"
                      className="w-full h-full -rotate-90"
                    >
                      <circle
                        cx="60"
                        cy="60"
                        r="52"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="12"
                        className="text-green-200"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="52"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="12"
                        strokeLinecap="round"
                        className="text-green-500"
                        strokeDasharray={`${(76.2 / 100) * (2 * Math.PI * 52)} ${2 * Math.PI * 52}`}
                        style={{ transition: "stroke-dasharray 1.2s ease-out" }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="font-heading text-2xl font-bold text-green-800">
                        {financialROI.netMarginPercent}%
                      </span>
                      <span className="text-[9px] text-green-600">margin</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div className="rounded-lg border border-background-100 bg-background-50 p-3">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Est. Reimbursement
                  </span>
                  <p className="text-sm font-bold text-foreground-800">
                    ${financialROI.insuranceReimbursement.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-3">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Total Est. Cost
                  </span>
                  <p className="text-sm font-bold text-foreground-800">
                    ${financialROI.totalEstimatedCost.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-3">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block mb-1">
                    Net Profit
                  </span>
                  <p className="text-sm font-bold text-green-700">
                    ${financialROI.netProfit.toLocaleString()}
                  </p>
                </div>
              </div>
            </section>

            {/* ===== SECTION 3: WHAT'S BEEN PROCESSED ===== */}
            <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-check-double-line text-xs"></i>
                </span>
                <div>
                  <h2 className="font-heading text-sm md:text-base font-bold text-foreground-950">
                    What's Been Processed
                  </h2>
                  <p className="text-[10px] md:text-[11px] text-foreground-400">
                    All intake and clinical data collection steps completed
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {processedItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg border border-background-100 bg-background-50"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[10px]"></i>
                    </span>
                    <div>
                      <p className="text-xs md:text-sm font-semibold text-foreground-800">
                        {item.label}
                      </p>
                      <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed mt-0.5">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* ===== RIGHT COLUMN: SWO / L-Code Allowable Schedule → Expense Breakdown ===== */}
          <div className="flex flex-col space-y-5">
            {/* ===== SECTION 4: STANDARD WORK ORDER — L-Code Allowable Schedule ===== */}
            <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-7">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-clipboard-line text-xs"></i>
                </span>
                <div>
                  <h2 className="font-heading text-sm md:text-base font-bold text-foreground-950">
                    Standard Work Order
                  </h2>
                  <p className="text-[10px] md:text-[11px] text-foreground-400">
                    L-Code Allowable Schedule — Medicare PDAC Verified
                  </p>
                </div>
              </div>

              {/* Pre-Auth Status Banner */}
              <div className="flex items-center gap-2.5 mb-5 p-3 rounded-lg border border-accent-200 bg-accent-50/60">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-time-line text-[10px]"></i>
                </span>
                <div>
                  <span className="text-[10px] font-semibold text-accent-800 block">
                    Pre-Authorization:{" "}
                    {financialROI.insuranceCoverage.preAuthStatus}
                  </span>
                  <span className="text-[9px] text-foreground-500">
                    Ref: {financialROI.insuranceCoverage.preAuthorizationRef} ·{" "}
                    {financialROI.insuranceCoverage.provider}{" "}
                    {financialROI.insuranceCoverage.planType}
                  </span>
                </div>
              </div>

              {/* L-Code Table */}
              <div className="rounded-xl border border-background-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-background-100 bg-background-50">
                      <th className="px-3 md:px-4 py-2.5 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                        L-Code
                      </th>
                      <th className="px-3 md:px-4 py-2.5 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                        Description
                      </th>
                      <th className="hidden sm:table-cell px-3 md:px-4 py-2.5 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                        Category
                      </th>
                      <th className="px-3 md:px-4 py-2.5 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap text-right">
                        Allowable
                      </th>
                      <th className="px-3 md:px-4 py-2.5 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap text-center">
                        Qty
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialROI.lCodeBreakdown.map((item, idx) => (
                      <tr
                        key={item.code}
                        className={`border-b border-background-50 ${idx % 2 === 0 ? "bg-white" : "bg-background-50/50"}`}
                      >
                        <td className="px-3 md:px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 rounded-md bg-accent-50 border border-accent-200 text-[10px] font-mono font-bold text-accent-700 whitespace-nowrap">
                            {item.code}
                          </span>
                        </td>
                        <td className="px-3 md:px-4 py-3">
                          <span className="text-[10px] md:text-[11px] text-foreground-700 font-medium leading-snug">
                            {item.description}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell px-3 md:px-4 py-3">
                          <span className="text-[10px] text-foreground-500 whitespace-nowrap">
                            {item.category}
                          </span>
                        </td>
                        <td className="px-3 md:px-4 py-3 text-right">
                          <span className="text-[10px] md:text-[11px] font-bold text-foreground-800 tabular-nums whitespace-nowrap">
                            ${item.allowable.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-3 md:px-4 py-3 text-center">
                          <span className="text-[10px] text-foreground-500 tabular-nums">
                            {item.units}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="bg-accent-50/40">
                      <td
                        colSpan={3}
                        className="hidden sm:table-cell px-3 md:px-4 py-3"
                      >
                        <span className="text-[10px] md:text-[11px] font-bold text-foreground-800">
                          Total Estimated Allowable
                        </span>
                      </td>
                      <td className="sm:hidden px-3 md:px-4 py-3">
                        <span className="text-[10px] md:text-[11px] font-bold text-foreground-800">
                          Total
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 text-right">
                        <span className="text-xs md:text-sm font-bold text-accent-800 tabular-nums whitespace-nowrap">
                          $
                          {financialROI.lCodeBreakdown
                            .reduce(
                              (sum, lc) => sum + lc.allowable * lc.units,
                              0,
                            )
                            .toLocaleString()}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Insurance coverage summary */}
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg border border-background-100 bg-background-50 p-2.5 text-center">
                  <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block mb-0.5">
                    Coverage
                  </span>
                  <p className="text-[11px] font-bold text-foreground-800">
                    {financialROI.insuranceCoverage.coveragePercent}%
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-2.5 text-center">
                  <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block mb-0.5">
                    Deductible Left
                  </span>
                  <p className="text-[11px] font-bold text-foreground-800">
                    $
                    {financialROI.insuranceCoverage.deductibleRemaining.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-2.5 text-center">
                  <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block mb-0.5">
                    OOP Remaining
                  </span>
                  <p className="text-[11px] font-bold text-foreground-800">
                    $
                    {financialROI.insuranceCoverage.outOfPocketRemaining.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-background-100 bg-background-50 p-2.5 text-center">
                  <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block mb-0.5">
                    Policy Status
                  </span>
                  <p className="text-[11px] font-bold text-green-700">
                    {financialROI.insuranceCoverage.policyStatus}
                  </p>
                </div>
              </div>

              {/* Notes */}
              <p className="text-[9px] text-foreground-400 mt-3 leading-relaxed">
                {financialROI.insuranceCoverage.notes}
              </p>
            </section>

            {/* ===== SECTION 5: EXPENSE BREAKDOWN ===== */}
            <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-foreground-100 text-foreground-600">
                  <i className="ri-bank-card-line text-xs"></i>
                </span>
                <div>
                  <h2 className="font-heading text-sm md:text-base font-bold text-foreground-950">
                    Expense Breakdown
                  </h2>
                  <p className="text-[10px] md:text-[11px] text-foreground-400">
                    AI-aggregated vendor pricing across contracted catalogs
                  </p>
                </div>
              </div>

              {/* Data Expense Table */}
              <div className="rounded-xl border border-background-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-background-100 bg-background-50">
                      <th className="px-4 md:px-5 py-3 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                        Expense Category
                      </th>
                      <th className="px-4 md:px-5 py-3 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap text-right">
                        Amount
                      </th>
                      <th className="hidden sm:table-cell px-4 md:px-5 py-3 text-[9px] md:text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Wholesale Materials */}
                    <tr className="border-b border-background-50">
                      <td className="px-4 md:px-5 py-3.5">
                        <span className="text-xs md:text-sm font-semibold text-foreground-700 whitespace-nowrap">
                          Wholesale Materials Cost
                        </span>
                      </td>
                      <td className="px-4 md:px-5 py-3.5 text-right">
                        <span className="text-xs md:text-sm font-bold text-foreground-700 tabular-nums whitespace-nowrap">
                          $
                          {financialROI.wholesaleMaterialsCost.toLocaleString()}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-4 md:px-5 py-3.5">
                        <span className="text-[10px] md:text-[11px] text-foreground-500">
                          Carbon fiber socket, dynamic foot module, pylon,
                          suspension sleeve, interface liner, cosmetic cover
                        </span>
                      </td>
                    </tr>

                    {/* HIGHLIGHTED: Data Lease Fee */}
                    <tr className="bg-accent-50">
                      <td className="px-4 md:px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="w-1 h-8 rounded-full bg-accent-500 flex-shrink-0"></span>
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                            <i className="ri-star-fill text-[9px]"></i>
                          </span>
                          <div className="min-w-0">
                            <span className="text-xs md:text-sm font-bold text-accent-900 whitespace-nowrap block">
                              Quick Prosthetics Data Lease Fee
                            </span>
                            <span className="text-[9px] text-accent-600 hidden sm:inline">
                              AI-generated patient file, 3D limb metrics,
                              clinical artifact suite, sourcing forecast
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-5 py-3.5 text-right">
                        <span className="text-base md:text-lg font-bold text-accent-800 tabular-nums whitespace-nowrap">
                          ${financialROI.dataLeaseFee.toLocaleString()}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-4 md:px-5 py-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-500 text-[8px] font-bold text-background-50 uppercase tracking-wider whitespace-nowrap">
                          Revenue Highlight
                        </span>
                      </td>
                    </tr>

                    {/* Total Estimated Cost */}
                    <tr className="bg-background-50">
                      <td className="px-4 md:px-5 py-3.5">
                        <span className="text-xs md:text-sm font-bold text-foreground-900 whitespace-nowrap">
                          Total Estimated Build Cost
                        </span>
                      </td>
                      <td className="px-4 md:px-5 py-3.5 text-right">
                        <span className="text-xs md:text-sm font-bold text-foreground-900 tabular-nums whitespace-nowrap">
                          ${financialROI.totalEstimatedCost.toLocaleString()}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-4 md:px-5 py-3.5">
                        <span className="text-[10px] md:text-[11px] text-foreground-500">
                          Aggregated across contracted vendor catalogs.
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Bottom context */}
              <p className="text-[10px] text-foreground-400 mt-4 leading-relaxed">
                Financial projections are AI-estimated based on current Medicare
                L-Code fee schedules and aggregated vendor catalog pricing.
                Actual reimbursement varies by payer contract, geographic
                adjustment, and prior authorization status.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Compliance Footer */}
      <footer className="w-full bg-background-100 border-t border-background-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
              <i className="ri-information-line text-[11px]"></i>
            </span>
            <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
              {globalComplianceText}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
